/**
 * Toast - Simple toast notification system with provider and hook
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react';

// ========================
// Types
// ========================

type ToastType = 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

export interface ToastActions {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  /** Alias for showSuccess - supports destructuring as { success } */
  success: (message: string) => void;
  /** Alias for showError - supports destructuring as { error } */
  error: (message: string) => void;
  /** Alias for showWarning - supports destructuring as { warning } */
  warning: (message: string) => void;
}

// ========================
// Context
// ========================

const ToastContext = createContext<ToastActions | null>(null);

// ========================
// Constants
// ========================

const AUTO_DISMISS_MS = 5000;

let toastCounter = 0;

const typeConfig: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
  },
};

const typeIcons: Record<ToastType, React.FC<{ className?: string }>> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
};

// ========================
// ToastProvider
// ========================

export interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string) => {
      toastCounter += 1;
      const id = `toast-${toastCounter}`;
      setToasts(prev => [...prev, { id, type, message }]);
      setTimeout(() => removeToast(id), AUTO_DISMISS_MS);
    },
    [removeToast],
  );

  const successFn = useCallback((msg: string) => addToast('success', msg), [addToast]);
  const errorFn = useCallback((msg: string) => addToast('error', msg), [addToast]);
  const warningFn = useCallback((msg: string) => addToast('warning', msg), [addToast]);

  const actions: ToastActions = {
    showSuccess: successFn,
    showError: errorFn,
    showWarning: warningFn,
    success: successFn,
    error: errorFn,
    warning: warningFn,
  };

  return (
    <ToastContext.Provider value={actions}>
      {children}
      {/* Toast container */}
      <div className='fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm'>
        {toasts.map(toast => {
          const config = typeConfig[toast.type];
          const Icon = typeIcons[toast.type];
          return (
            <div
              key={toast.id}
              className={`${config.bg} ${config.border} border rounded-lg p-4 shadow-lg flex items-start gap-3 animate-in slide-in-from-right`}
              role='alert'
            >
              <Icon className={`w-5 h-5 ${config.text} flex-shrink-0 mt-0.5`} />
              <p className={`text-sm ${config.text} flex-1`}>{toast.message}</p>
              <button
                type='button'
                onClick={() => removeToast(toast.id)}
                className={`${config.text} hover:opacity-70 flex-shrink-0`}
                aria-label='Dismiss notification'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

// ========================
// Hook
// ========================

export function useToastActions(): ToastActions {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastActions must be used within a ToastProvider');
  }
  return context;
}

export default ToastProvider;
